import React, { useCallback, useEffect, useState } from "react";
import { StaticGoogleMap, Marker as StaticMarker, Path as StaticPath } from "react-static-google-map";

import { generateRandomId, getGoogleKey, getZoomLevel, getZoomLevelFromBounds } from "utils";

import { LazyLoadImage } from "components";

export const MapStatic = React.memo(({ defaultZoom = null, field, geometry, marker, location, iconUrl = null }) => {
  const [zoom, setZoom] = useState(15);
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const [mapId, setMapId] = useState(null);

  const { lat, lng } = location || { lat: 0, lng: 0 };

  const icons = [
    "https://shared.klim.eco/maps/green_2_s.png",
    "https://shared.klim.eco/maps/green_2_m.png",
    "https://shared.klim.eco/maps/green_2_l.png",
    "https://shared.klim.eco/maps/red_s.png",
    "https://shared.klim.eco/maps/red_m.png",
    "https://shared.klim.eco/maps/red_l.png",
  ];

  const updateZoom = useCallback(() => {
    if (defaultZoom) {
      setZoom(defaultZoom);
    } else if (geometry && geometry.geometry) {
      // Calculate zoom based on geometry bounds to ensure entire shape is visible
      // Use mini mode for small containers to avoid Google logo being oppressive
      setZoom(getZoomLevelFromBounds(geometry.geometry));
    } else if (field) {
      setZoom(getZoomLevel(field, width * height <= 100000)); // set to mini level if image small
    }
  }, [defaultZoom, field, geometry, width, height]);

  useEffect(() => {
    // note; for small images, the google logo is oppressive
    // so we calc the aspect ratio of the container
    // and either use container dimensions or the min of 100px * aspect ratio

    if (!mapId) return;

    const containerWidth = document.getElementById(mapId).getBoundingClientRect().width;
    const containerHeight = document.getElementById(mapId).getBoundingClientRect().height;

    if (containerWidth > 0 && containerHeight > 0) {
      const aspectRatio = containerHeight / containerWidth;

      const useMinVals = containerWidth <= 125 || containerHeight <= 125;

      const newWidth = useMinVals ? 125 : containerWidth;
      const newHeight = useMinVals ? 125 * aspectRatio : containerHeight;

      setWidth(newWidth);
      setHeight(newHeight);
    } else {
      setTimeout(() => {
        const id = generateRandomId();
        setMapId(id);
      }, 1000);
    }
  }, [mapId, width, height, zoom]);

  useEffect(() => {
    if (mapId) return;

    const id = generateRandomId();
    setMapId(id);
  }, [lat, lng, zoom, field?.id, mapId]);

  useEffect(() => {
    updateZoom();
  }, [lat, lng, updateZoom]);

  return (
    <div className="static-map-card" id={mapId}>
      {height > 0 && width > 0 && (
        <StaticGoogleMap
          key={mapId}
          as={(props) => <LazyLoadImage {...props} delay={2000} />}
          scale={2}
          size={`${parseInt(width)}x${parseInt(height)}`}
          apiKey={getGoogleKey()}
          center={
            (geometry && geometry.center && `${geometry.center.lat},${geometry.center.lng}`) ||
            (field && `${field.location.lat},${field.location.lng}`) ||
            `${location.lat},${location.lng}`
          }
          zoom={zoom}
          maptype="satellite"
        >
          {geometry && geometry.geometry && <StaticPath color={"0xFFFFFFFF"} fillcolor={"0xFFFFFF33"} points={geometry.geometry} weight={2} />}
          {marker && (
            <StaticMarker
              iconURL={iconUrl ?? icons[1]}
              location={
                geometry && geometry.center
                  ? `${geometry.center.lat},${geometry.center.lng}`
                  : field
                  ? `${field.location.lat},${field.location.lng}`
                  : `${location.lat},${location.lng}`
              }
              label={marker.label && marker.label}
            />
          )}
        </StaticGoogleMap>
      )}
    </div>
  );
});

export default MapStatic;
